/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MaterialReactTable, MRT_Cell, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { Box, Typography, IconButton, Tooltip, useTheme, Chip, Skeleton } from '@mui/material';
import infoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';

interface NumericalAnalysisData {
  fleetId: string;
  date: string;
  safetyLabel: 'High' | 'Medium' | 'Low';
  eventsWarnings: number;
  distance: number;
  eventsPerDistance: number;
  flag: boolean;
}

interface NumericalAnalysisTableProps {
  startDate?: string;
  endDate?: string;
  fleetId?: string;
  paginatedData?: any;
  onPageChange?: (cursor: any) => void;
  infoIconTooltip?: string;
}

const NumericalAnalysisTable: React.FC<NumericalAnalysisTableProps> = ({
  startDate,
  endDate,
  fleetId,
  paginatedData,
  onPageChange,
  infoIconTooltip,
}) => {
  const theme = useTheme();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pagesData, setPagesData] = useState<NumericalAnalysisData[][]>([]);
  const [nextCursor, setNextCursor] = useState<any>(null);
  const [hasMorePages, setHasMorePages] = useState(false);
  const { user } = useAuth();

  // Mock data for now - replace with actual API call
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset pagination when filters change (but not when paginatedData changes)
    if (startDate || endDate || fleetId) {
      setCurrentPage(1);
      setPagesData([]);
      setNextCursor(null);
      setHasMorePages(false);
      setLoading(true);
    }
  }, [startDate, endDate, fleetId]);

  useEffect(() => {
    // Process paginated data when it changes
    if (paginatedData && paginatedData.data) {
      const transformedData: NumericalAnalysisData[] = paginatedData.data.map((item: any) => {
        // Use safety_label directly from API response
        const safetyLabel =
          ((item.safety_label?.charAt(0).toUpperCase() + item.safety_label?.slice(1).toLowerCase()) as
            | 'High'
            | 'Medium'
            | 'Low') || 'Low';
        const rateOfWarnings = item.rate_of_warnings;

        return {
          fleetId: item.vehicle_id,
          date: item.record_date,
          safetyLabel: safetyLabel,
          eventsWarnings: item.total_warning_count,
          distance: parseFloat(item.distance_km.toFixed(2)),
          eventsPerDistance: rateOfWarnings !== null ? parseFloat(rateOfWarnings.toFixed(2)) : 0,
          flag: safetyLabel === 'High', // Flag for High risk
        };
      });

      // Store the current page data
      setPagesData((prevPages) => {
        const updatedPages = [...prevPages];
        updatedPages[currentPage - 1] = transformedData;
        return updatedPages;
      });

      // Store next_cursor if it exists
      if (paginatedData.next_cursor) {
        setNextCursor(paginatedData.next_cursor);
        setHasMorePages(true);
      } else {
        setNextCursor(null);
        setHasMorePages(false);
      }

      setLoading(false);
    }
  }, [paginatedData]);

  const cumulativeCount = useMemo(() => {
    return pagesData.slice(0, currentPage).reduce((sum, page) => sum + (page?.length || 0), 0);
  }, [pagesData, currentPage]);

  const currentPageData: NumericalAnalysisData[] = pagesData[currentPage - 1] ?? [];

  // Create skeleton data for loading state (4 rows)
  const skeletonData: NumericalAnalysisData[] = Array(4)
    .fill(null)
    .map((_, index) => ({
      fleetId: `skeleton-${index}`,
      date: '2025-01-01',
      safetyLabel: 'Low' as 'High' | 'Medium' | 'Low',
      eventsWarnings: 0,
      distance: 0,
      eventsPerDistance: 0,
      flag: false,
    }));

  // Use skeleton data if loading or no data available, otherwise use actual data
  const displayData = loading || currentPageData.length === 0 ? skeletonData : currentPageData;

  const getSafetyLabelColor = (label: string) => {
    switch (label) {
      case 'High':
        return { background: '#D9EDE6', color: '#219653' }; // Green
      case 'Medium':
        return { background: '#FFF4E5', color: '#FF9800' }; // Orange
      case 'Low':
        return { background: '#F7E1E1', color: '#C83333' }; // Red
      default:
        return { background: '#F5F5F5', color: '#9E9E9E' }; // Grey
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'fleetId',
        header: 'Vehicle ID',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={100}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<string>() || 'N/A'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'date',
        header: 'Date',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={120}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          const dateValue = cell.getValue<string>();
          const formatDate = (dateString: string) => {
            if (!dateString) return 'N/A';
            try {
              const date = new Date(dateString);
              const day = date.getDate();
              const monthNames = [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
              ];
              const month = monthNames[date.getMonth()];
              const year = date.getFullYear();
              return `${day} ${month} ${year}`;
            } catch {
              return dateString;
            }
          };
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {formatDate(dateValue)}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'safetyLabel',
        header: 'Safety Label',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="rounded"
                width={60}
                height={24}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  borderRadius: '8px',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          const value = cell.getValue<string>();
          const colors = getSafetyLabelColor(value);
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                backgroundColor: colors.background,
                color: colors.color,
                fontWeight: 500,
                height: '24px',
                gap: '10px',
                opacity: 1,
                borderRadius: '8px',
                paddingTop: '4px',
                paddingRight: '8px',
                paddingBottom: '4px',
                paddingLeft: '8px',
                '& .MuiChip-label': {
                  padding: 0,
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: 'eventsWarnings',
        header: 'Events (Warnings)',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={80}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()?.toLocaleString() ?? 0}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'distance',
        header: 'Distance',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={80}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()?.toFixed(2)} km
            </Typography>
          );
        },
      },
      {
        accessorKey: 'eventsPerDistance',
        header: 'Events/Distance',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={100}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()?.toFixed(2)}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'flag',
        header: 'Flag',
        Cell: ({ row }: { row: any }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="circular"
                width={12}
                height={12}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          const safetyLabel = row.original.safetyLabel;
          const colors = getSafetyLabelColor(safetyLabel);
          return (
            <Box display="flex">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: colors.color,
                }}
              />
            </Box>
          );
        },
      },
    ],
    [loading, currentPageData],
  );

  const tableOptions: MRT_TableOptions<NumericalAnalysisData> = {
    columns: columns,
    data: displayData,
    enableColumnActions: false,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    layoutMode: 'semantic',
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
        width: '100%',
        '& .MuiTableRow-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
        '& .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          padding: '0px 12px !important',
          verticalAlign: 'middle !important',
          boxSizing: 'border-box !important',
          // lineHeight: '1 !important',
        },
        '& .MuiTableHead-root .MuiTableRow-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          boxShadow: 'none !important',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
        '& .MuiTableBody-root .MuiTableRow-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        width: '100%',
        maxWidth: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '3px',
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
    muiTableHeadProps: {
      sx: {
        backgroundColor: '#F6F6FD',
        border: '1px solid #F0EFF8',
        borderRadius: '8px',
        height: '48px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0px',
        fontWeight: 600,
        fontStyle: 'normal',
        fontSize: '14px',
        // lineHeight: '21px',
        letterSpacing: '0%',
        color: '#1B1B1B',
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        padding: '0px 12px !important',
        verticalAlign: 'middle',
        '&:first-of-type': {
          borderRadius: '8px 0 0 8px',
        },
        '&:last-of-type': {
          borderRadius: '0 8px 8px 0',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        '& .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          verticalAlign: 'middle',
          padding: '0px 12px !important',
        },
        backgroundColor:
          row.index % 2 === 0 ? (theme.palette.mode === 'dark' ? '#121212' : '#F0EFF8') : 'inherit',
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        padding: '0px 12px !important',
        verticalAlign: 'middle',
        // lineHeight: '1 !important',
        boxSizing: 'border-box !important',
      },
    },
  };

  const tableInstance = useMaterialReactTable(tableOptions);

  // Calculate total pages based on available data and hasMorePages flag
  const totalPages = hasMorePages ? currentPage + 1 : currentPage;

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    // Check if we already have data for this page
    if (pagesData[page - 1]) {
      // Data already exists, just change page
      setCurrentPage(page);
    } else if (page > currentPage && onPageChange && nextCursor) {
      // Need to fetch new page data
      setLoading(true);
      setCurrentPage(page);
      onPageChange(nextCursor);
    } else {
      // Fallback
      setCurrentPage(page);
    }
  };

  if (error) {
    return (
      <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
      {/* Header */}
      <Box className="w-full" display="flex" justifyContent="space-between" alignItems="center">
        <Box className="w-full" display="flex" alignItems="center" gap={1}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '27px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            Numerical Analysis of Recent Week
          </Typography>
          <Tooltip title={infoIconTooltip} arrow>
            <img src={infoIcon} alt="info icon" width={16} height={16} />
          </Tooltip>
        </Box>
        {/* <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="medium" sx={{ p: 0 }}>
            <img src={menuIcon} alt="Table" />
          </IconButton>
        </Box> */}
      </Box>

      {/* Scrollable table container or No Data placeholder */}
      {!loading && currentPageData.length === 0 ? (
        <Box
          sx={{
            width: '100%',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
        </Box>
      ) : (
        <div className="table-wrapper" style={{ overflow: 'auto', width: '100%' }}>
          <MaterialReactTable table={tableInstance} />
        </div>
      )}

      {/* Pagination - Only show if there's data */}
      {!loading && currentPageData.length > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} gap={1} mt={2}>
          <IconButton
            size="small"
            onClick={() => handlePageChange(null as any, currentPage - 1)}
            disabled={currentPage === 1}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              '&:disabled': {
                opacity: 0.5,
                borderColor: '#ccc',
              },
            }}
          >
            <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
          </IconButton>

          {/* Page numbers - sliding window of 3 */}
          {(() => {
            const visiblePages: number[] = [];
            const windowSize = 3;

            // Calculate the start of the window
            let startPage = Math.max(1, currentPage - 1);

            // Adjust if we're near the end
            if (currentPage === totalPages && totalPages > 1) {
              startPage = Math.max(1, totalPages - windowSize + 1);
            }

            // Generate visible page numbers
            for (let i = 0; i < windowSize && startPage + i <= totalPages; i++) {
              visiblePages.push(startPage + i);
            }

            return visiblePages.map((pageNum) => (
              <Box
                key={pageNum}
                onClick={() => handlePageChange(null as any, pageNum)}
                sx={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: pageNum === currentPage ? theme.palette.primary.main : 'transparent',
                  color: pageNum === currentPage ? '#FFFFFF' : '#808080',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  textAlign: 'center',
                  '&:hover': {
                    backgroundColor: pageNum === currentPage ? theme.palette.primary.main : '#F5F5F5',
                  },
                }}
              >
                {pageNum}
              </Box>
            ));
          })()}

          <IconButton
            size="small"
            onClick={() => handlePageChange(null as any, currentPage + 1)}
            disabled={!hasMorePages}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              '&:disabled': {
                opacity: 0.5,
                borderColor: '#ccc',
              },
            }}
          >
            <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default NumericalAnalysisTable;
